<template>
    <div rating question :id="`question-${questionData.number}`" @keyup.enter="submitQuestion">
        <questionheader :questionData="questionData"></questionheader>
        <div data-question-main>
            <div
                :disabled="outerFieldIsChecked"
                :id="`question-${questionData.number}_graph-${questionData.number}`"
            >
                <strong data-only-screenreader>{{get.textResource(`rs_options`)}}</strong>
                <section v-if="graphType === `donut`">
                    <span :data-selected-segments="selectedSegments"></span>
                    <svg width="300" height="300" :id="`donut-question-${questionData.number}`">
                        <g
                            v-for="(answer, index) in segments"
                            :key="`donut-${index}`"
                            :disabled="editingAllowed"
                        >
                            <!-- 
                                For every segment(answer) a circle is added. With the dasharray and dashoffset attributes only a part
                                of a circle is shown, and together they will form a full circle
                            -->
                            <!-- First circle is used for the border around the circle -->
                            <circle
                                :cx="donut.cx"
                                :cy="donut.cy"
                                :r="donut.radius"
                                :stroke-width="selectedSegments - 1 < index ? donut.strokeWidth + 2 : 0"
                                :stroke-dasharray="circumference"
                                :stroke-dashoffset="calculateStrokeDashOffset"
                                :transform="donutTansformDegrees(index)"
                                :id="`circle-border`"
                                aria-hidden="true"
                            />
                            <circle
                                :cx="donut.cx"
                                :cy="donut.cy"
                                :r="donut.radius"
                                :stroke-width="selectedSegments - 1 < index ? donut.strokeWidth : 0"
                                :stroke-dasharray="circumference"
                                :stroke-dashoffset="calculateStrokeDashOffset"
                                :transform="donutTansformDegrees(index)"
                                :id="`donut-question-${questionData.number}_answer-${answer.number}`"
                                @mouseover="segmentMouseenter((index))"
                                @mouseleave="segmentMouseenter((-1))"
                                @focus="segmentMouseenter(index)"
                                @click="segmentClick(index)"
                                @keyup.space="segmentClick(index)"
                                :tabindex="editingAllowed ? -1 : 0"
                                :aria-selected="answer.isChecked"
                            />
                            <font-awesome-icon
                                data-icon
                                :x="segmentData[index].iconX"
                                :y="segmentData[index].iconY"
                                width="20px"
                                height="20px"
                                color="#ffffff"
                                icon="star"
                                aria-hidden="true"
                            />
                        </g>
                    </svg>
                    <img
                        :src="`${state.settings.assetPath}/rating-question.svg`"
                        :alt="get.textResource(`rs_rating_question_visual_aid_alt_tag`)"
                        aria-hidden="true"
                        data-visual-aid
                    />
                </section>
                <section v-else-if="graphType === `rectangle`">
                    <span :data-selected-segments="selectedSegments"></span>
                    <svg
                        height="75"
                        :width="rectGraphSize"
                        :id="`rectangle-question-${questionData.number}_answer-${questionData.number}`"
                    >
                        <!-- The background of the rectangle -->
                        <rect
                            data-background-rect
                            height="25px"
                            :y="rect.topMargin"
                            rx="15px"
                            :width="`${segments.length * rect.segmentWidth}px`"
                            :disabled="editingAllowed"
                        />
                        <!-- The selected part of the rectangle -->
                        <rect
                            data-background-selected-rect
                            height="56px"
                            y="15px"
                            rx="30px"
                            :width="`${selectedSegments * rect.segmentWidth}px`"
                            :disabled="editingAllowed"
                        />
                        <g
                            v-for="(answer, index) in segments"
                            :key="`rect-${answer.id}`"
                            :disabled="editingAllowed"
                            @mouseover="segmentMouseenter((index))"
                            @mouseleave="segmentMouseenter((-1))"
                            @focus="segmentMouseenter((index))"
                            @click="segmentClick((index), $event)"
                            @keyup.space="segmentClick((index), $event)"
                            :tabindex="(editingAllowed ? -1 : 0)"
                            :aria-selected="answer.isChecked"
                        >
                            <rect
                                :x="`${rect.segmentWidth * index}px`"
                                :y="15"
                                :id="`rectangle-question-${questionData.number}_answer-${answer.number}`"
                                data-segment
                                rx="30px"
                                :aria-label="answer.label"
                                :style="{ 
                                    height: `56px`,
                                    width: `${rect.segmentWidth}px`
                                }"
                            />
                            <font-awesome-icon
                                data-icon
                                :x="segmentData[index].iconX"
                                :y="segmentData[index].iconY"
                                width="20px"
                                height="20px"
                                color="#ffffff"
                                :id="`rectangle-question-${questionData.number}_icon-${answer.number}`"
                                icon="star"
                            />
                        </g>
                    </svg>
                </section>
            </div>
            <div v-if="questionData.settings.showLabels && selectedAnswer" data-selected-label>
                <p
                    v-html="$sanitize(selectedAnswer.label)"
                    :id="`question-${questionData.number}_label`"
                ></p>
            </div>
            <checkboxradio
                v-if="outerField"
                :questionData="questionData"
                :outerfield="true"
                :answerData="outerField"
                :showLabel="true"
                :disabled="!get.editingAllowed(questionData, false) || state.global.showLoader"
                type="checkbox"
                @element-click="outerFieldClicked"
            />
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>
<script>
import mixins from "@/global/mixins";
export default {
    name: `rating-question`,
    mixins: [mixins.defaultQuestion],
    data() {
        return {
            segmentData: [],
            graphType: ``,
            segmentIsClicked: false,
            rect: {
                segmentWidth: 56,
                topMargin: 30,
            },
            donut: {
                cx: 150,
                cy: 150,
                radius: 120, // The size of the donut
                strokeWidth: 42, // How thick the donut has to be
                angleOffset: -230, // Where the opening of the donout should be
                degrees: 280, // How many degrees the donut should be. The rest forms the gap
                segmentPercentageAdjustment: 27.8, // This is needed because the donut is nog 360 degrees. Update this number when the amount of degrees is adjusted
            },
        };
    },
    computed: {
        segmentAmt: function () {
            return this.segments.length;
        },
        circumference() {
            return 2 * Math.PI * this.donut.radius;
        },
        segmentPercentage() {
            return 100 / (this.segmentAmt * (100 + this.donut.segmentPercentageAdjustment));
        },
        selectedAnswer() {
            return this.segments[this.selectedSegments - 1];
        },
        selectedSegments() {
            return this.segments.findIndex((answer) => answer.isChecked) + 1;
        },
        segments() {
            return this.questionData.answers.filter((answer) => answer.type === `None`);
        },
        outerField() {
            return this.questionData.answers.find((answer) => answer.type === `OuterField`);
        },
        rectGraphSize() {
            return this.segmentAmt * this.rect.segmentWidth;
        },
        answersGiven() {
            return this.questionData.answers.filter((answer) => answer.isChecked).length;
        },
        editingAllowed: function () {
            return !this.get.editingAllowed(this.questionData) || this.state.global.showLoader;
        },
        outerFieldIsChecked: function () {
            return this.outerField !== undefined && this.outerField.isChecked;
        },
        // Calculate what percentage of the donut should be shown
        calculateStrokeDashOffset() {
            let strokeDiff = this.segmentPercentage * this.circumference;
            return this.circumference - strokeDiff;
        },
    },
    methods: {
        // The rectangle segments should be displayed in reverse order into their 'use' containers. This method reverses the order of the id's
        reverseIndex: function (index) {
            return this.segmentAmt - index - 1;
        },
        segmentClick: function (segmentIndex) {
            this.segmentIsClicked = true;
            this.checkSegments(segmentIndex);
            this.$store.dispatch(`autoNext`, { questionData: this.questionData });
        },
        /* Calculate the position for each segment and its label in the rectangle */
        calculateRectChartData() {
            let labelY = this.rect.topMargin + 2;
            let labelXDeviation = this.rect.segmentWidth / 2 - 10;

            for (let i = 0; i < this.segmentAmt; i++) {
                this.segmentData.push({
                    iconX: i * this.rect.segmentWidth + labelXDeviation,
                    iconY: labelY,
                });
            }
        },
        /* Calculate the position for each segment and its label in the donut */
        calculateDonutChartData() {
            let tempAngleOffset = this.donut.angleOffset;

            for (let i = 0; i < this.segmentAmt; i++) {
                let angleOffset = this.donut.angleOffset;
                let { x, y } = this.calculateDonutIconCoords(angleOffset);

                this.segmentData.push({
                    degrees: angleOffset,
                    iconX: x,
                    iconY: y,
                });
                this.donut.angleOffset = this.donut.degrees / this.segmentAmt + angleOffset;
            }

            // Reset the angleOffset so that when everything has to be calculated again, the offset has its initial value again
            this.donut.angleOffset = tempAngleOffset;
        },
        outerFieldClicked() {
            this.outerField.isChecked = !this.outerField.isChecked;
            this.$store.dispatch(`autoNext`, { questionData: this.questionData });
        },
        /* Apply the rotation on the donut */
        donutTansformDegrees(index) {
            let degrees = this.segmentData[index].degrees;

            return `rotate(${degrees}, ${this.donut.cx}, ${this.donut.cy})`;
        },
        /* Calculate the positions of the icons in the donut */
        calculateDonutIconCoords(angleOffset) {
            let donut = this.donut;
            let angle = (this.segmentPercentage * 360) / 2 + angleOffset;
            let radians = angle * (Math.PI / 180);

            return {
                x: donut.radius * Math.cos(radians) + donut.cx - 10,
                y: donut.radius * Math.sin(radians) + donut.cy - 12,
            };
        },
        segmentMouseenter(segmentIndex) {
            if (!this.segmentIsClicked) this.checkSegments(segmentIndex);
        },
        /* When the 7th segment is clicked, all the segment before that also have to be checked, that is how the question model works */
        checkSegments(segmentIndex) {
            this.segments.forEach((answer) => (answer.isChecked = false));
            if (segmentIndex !== -1) this.segments[segmentIndex].isChecked = true;
        },
        /* Decide whether the graph should be a donut or a rectangle based on whether a rectangle would fit on the page or not */
        decideOnGraphType() {
            this.segmentData = [];

            if (this.questionData.settings.ratingOptionsCount >= 6 || this.questionData.settings.showAsCircle) {
                this.graphType = `donut`;
                this.calculateDonutChartData();
            } else {
                this.graphType = `rectangle`;
                this.calculateRectChartData();
            }
        },
        onResize() {
            clearTimeout(this.resizedFinished);

            this.resizedFinished = setTimeout(() => {
                this.decideOnGraphType();
            }, 250);
        },
    },
    mounted: function () {
        window.addEventListener("resize", this.onResize);
        this.decideOnGraphType();
    },
};
</script>
<style lang="scss" scoped>
$primary-color: --var(--primary-color);

@for $i from 1 through 12 {
    [data-selected-segments="#{$i}"] + {
        @for $j from 0 through $i {
            svg g:nth-of-type(#{$j}) {
                circle {
                    stroke: var(--primary-color);
                    stroke-width: 60;
                }

                rect {
                    fill: var(--primary-color);
                    height: 56px;
                }

                &[disabled] rect {
                    fill: #e4e4e4;
                }
            }
        }
    }
}

svg.fa-star {
    stroke: #878787;
    stroke-width: 17px;
}

[disabled] {
    pointer-events: none;

    circle {
        stroke: #d6d6d6 !important; // Disabled styling has to override all other colors
        pointer-events: none;
    }
    rect {
        pointer-events: none;
        stroke: none !important;
    }
}

[data-visual-aid] {
    height: 90px;
    position: absolute;
    top: 110px;
}

[data-answer] {
    flex-direction: column;

    svg path {
        color: #fff;
    }
}

[data-icon] {
    pointer-events: none;
}

[data-selected-label] p {
    width: 100%;
    text-align: center;
}

[data-background-selected-rect] {
    fill: var(--primary-color);

    &[disabled] {
        fill: #e4e4e4;
        cursor: none;
    }
}

[data-background-rect] {
    stroke: #878787;
}

[data-segment] {
    fill: transparent;
}

section {
    display: flex;
    justify-content: center;
    margin: 10px 0 20px 0;
    min-width: 300px;
    position: relative;
}

#circle-border {
    stroke: #878787;
}

[disabled] > #circle-border {
    stroke: #878787;
}

circle,
rect {
    cursor: pointer;
    transition: 0.1s;
}

circle {
    stroke: #ebebeb;
    pointer-events: stroke;
    fill: transparent;
    -webkit-tap-highlight-color: transparent;
 
    &:focus {
        outline: 0;
        stroke-opacity: 0.7;
    }
}

rect {
    fill: #ebebeb;
}

text {
    pointer-events: none;
}

[data-outerfield] {
    margin: 15px 0;
}
</style>